import { CountryApprovalData, CountryApproverData } from 'api/travel-request-detail';
import { autoinject, computedFrom, customElement } from 'aurelia-framework';
import { bindable } from 'aurelia-typed-observable-plugin';
import { ValidationRules } from 'aurelia-validation';
import { watch } from 'aurelia-watch-decorator';
import { DateValueConverter } from 'framework/value_converters/date-and-time';
import { UserInfo } from 'model/business-base';
import { ApprovalStatus } from 'model/business-enums';
import { Country } from 'model/business-keywords';
import { CountryApproval, CountryApprover } from 'model/forms/travel-request';
import { KeywordsService } from 'services/keywords-service';
import { SafetyService } from 'services/safety-service';
import { ApproveType } from 'api/swagger/ApiClient'

@autoinject
@customElement('country-approval-panel')
export class CountryApprovalPanelCustomElement {
  approver: UserInfo = null;
  @bindable country: Country;
  @bindable model: CountryApproval | CountryApprovalData;
  @bindable disabled: boolean;
  private rendered = false;

  constructor(private keywords: KeywordsService, private safety: SafetyService) { }

  bind() {
    this.approver = this.model.approvalTypeValue === ApproveType.One && this.model.approvers.length === 1 ? this.model.approvers[0].approver : null;
    this.bindValidation();
  }

  private bindValidation() {
    ValidationRules
      .ensureObject<CountryApprovalPanelCustomElement>().required()
      .ensure(component => component.approver).displayName('Approver').required().when(component => component.selectionRequired)
      .on(this);
  }

  attached() {
    this.rendered = true;
  }

  detached() {
    this.rendered = false;
  }

  @computedFrom('model.approvalTypeValue')
  get approverGroupLabel() {
    switch (this.model.approvalTypeValue) {
      case ApproveType.One: return 'Approver (selected approver required)';
      case ApproveType.Any: return 'Approvers (any listed approver required)';
      case ApproveType.All: return 'Approvers (all listed approvers required)';
      default: return 'Approvers';
    }
  }

  @computedFrom('model.approvalTypeValue')
  get selectionRequired() {
    return this.model.approvalTypeValue === ApproveType.One;
  }

  @computedFrom('safety.countrySettings', 'country', 'approver')
  get countryApprovers() {
    return this.keywords.ensureItem(this.safety.countrySettings.get(this.country.id).approvers, this.approver);
  }

  @computedFrom('model.approvers')
  get approverNames() {
    return (this.model.approvers as CountryApprover[]).map(a => a.approver.name).join(', ');
  }

  keywordsItemMatcher = this.keywords.itemsMatch;

  @watch((x: CountryApprovalPanelCustomElement) => x.approver)
  private approverChanged(approver: UserInfo) {
    if (this.rendered)
      approver != null ? this.model.approvers.splice(0, this.model.approvers.length, { approver: approver }) : this.model.approvers.splice(0);
  }

  @computedFrom('model.status')
  get statusClass() {
    return (this.model as CountryApprovalData).status != null ? `--${ApprovalStatus[(this.model as CountryApprovalData).status.value].toLowerCase()}` : null;
  }

  @computedFrom('model.approvers', 'model.status')
  get countryApproverData() {
    const completed = [ApprovalStatus.Approved, ApprovalStatus.Declined].includes((this.model as CountryApprovalData).status?.value);
    return (this.model.approvers as CountryApproverData[])
      .filter(a => !completed || a.status?.value === (this.model as CountryApprovalData).status?.value)
      .map(a => ({ name: a.approver.name, status: this.getStatusText(a), comment: a.comment }));
  }

  private getStatusText(data: CountryApproverData) {
    switch (data.status?.value) {
      case (ApprovalStatus.Pending): return 'hasn\'t responded yet';
      case (ApprovalStatus.Approved): return 'approved' + (data.modifiedOn == null ? '': ` on ${new DateValueConverter().toView(data.modifiedOn, true)}`);
      case (ApprovalStatus.Declined): return 'declined' + (data.modifiedOn == null ? '': ` on ${new DateValueConverter().toView(data.modifiedOn, true)}`);
      case (ApprovalStatus.RequireMoreInfo): return 'requires more info';
      default: return data.status?.name ?? null;
    }
  }
}
